import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { TableColumn, TableSettings } from "../../../../tableSettings";
import AbstractListView, {
  DataFetchResult,
  ExportRowRenderer,
  ListCustomFilterRenderer,
  ListOrderBy,
  ListRowRenderer
} from "../../../common/AbstractListView";

import constants from '../../../../constants';
import { Search } from "../../../../filtering";

import { NavigationType, openNewPage } from '../../../../util/navigationHelpers';

import {
  DateRenderer,
  DateTimeRenderer,
  FloatRenderer,
  NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,
  TextRenderer
} from '../../../../uiControls/SmartTableRenderers';
import UniqueLinkButton from '../../../../uiControls/UniqueLinkButton';
import PrimaryButton from '../../../../ui/PrimaryButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faPencilAlt, faPlus, faTrashAlt, } from '@fortawesome/free-solid-svg-icons';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import ApplicationContext from '../../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../../common/JobListViewContext';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../../ui/ActionButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import { JobNames } from "../../../../framework.core/JobNames";
import { paymentPrepList } from "../../tableSettings";
import ListViewShortcutMenu from '../../../common/ListViewShortcutMenu';
import { PaymentPrepListItem } from '../../../../fms.data/models/PaymentPrepListItem';
import * as XCol from '../../../../framework.core/XExportColumn';
import ToolboxMenuItem from '../../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../../ui/menu/Menu';
import JobListSuntekCommand from '../../../common/JobListSuntekCommand';
import { FmsSecTask } from '../../../../fms.core/constants';
import { findSecurityTask } from '../../../../util';

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();
  const operation = 'accounting';

  const selectedElement = jobListContext.selectedElement as PaymentPrepListItem;

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPaymentPrep/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    openNewPage(history, `/${operation}/acPaymentPrep/add`, NavigationType.newPage);
  }

  async function deleteEntity() {
    if (!selectedElement) {
      return;
    }

    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Confirm to Delete?</h3>
      </div>
    , false, 0, true, 'warning');

    if (doDelete) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.PaymentPrep, {
        id: selectedElement.id,
        pimsLinkId: 0
      });
      if (apiResult && apiResult.success) {
        jobListContext.reloadData();
      } else {
        const message = apiResult?.message ?? 'Cannot delete!';
        await showGlobalOkDialog(message);
      }
    }
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/${operation}/acPaymentPrep/edit/${id}`;
    const viewUrl = `/${operation}/acPaymentPrep/view/${id}`;
    if (jobListContext.hasOpenedScreenStartingWithUrlWithoutMe(`/${operation}/acPaymentPrep/edit`, editUrl)) {
      await showGlobalOkDialog(<p>Edit operation is already in progress</p>);
      return;
    }


    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/${operation}/acPaymentPrep/edit/${id}`;
    const viewUrl = `/${operation}/acPaymentPrep/view/${id}`;
    const listSec = findSecurityTask(appContext, FmsSecTask.AcctPaymentPrep);
    const urls = [viewUrl];
    if(listSec?.grantEdit) urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  async function copy() {
    if (!selectedElement) {
      return;
    }

    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPaymentPrep/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    const id = selectedElement.id;
    openNewPage(history, `/${operation}/acPaymentPrep/add?copyId=${id}`, NavigationType.newPage);
  }

  return { create, deleteEntity, edit, view, copy, operation };
}

function RightClickMenu({ dismiss }: { dismiss: VoidFunction }) {
  const { create, edit, view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctPaymentPrep);
  //const selectedElement = jobListContext.selectedElement as PaymentPrepListItem;

  const items = [
    { label: 'New', shortcut: 'F2', separator: false, hidden: !listSec?.grantAdd },
    { label: 'View', shortcut: 'F9', separator: !listSec?.grantEdit && !listSec?.grantDel },
    { label: 'Edit', shortcut: 'F3', separator: !listSec?.grantDel, hidden: !listSec?.grantEdit },
    { label: 'Delete', shortcut: '', separator: true, hidden: !listSec?.grantDel, /*disabled: selectedElement.void*/ },
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: 'Refresh', shortcut: 'F11', separator: true, newCol: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) => ({
    label: <ToolboxMenuItem name="paymentPrep-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
    hidden: i?.hidden ?? false,
   // disabled: i?.disabled ?? false,
  }));

  function onMenuItemSelected(item: IfmsMenuItem) {
    switch (item.index) {
      case 0:
        create();
        break;
      case 1:
        view();
        break;
      case 2:
        edit();
        break;
      case 3:
        //deleteEntity();
        edit();
        break;
      case 4:
        jobListContext.triggerTableSearch('F5');
        break;
      case 5:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const selectedElement = jobListContext.selectedElement as PaymentPrepListItem;
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctPaymentPrep);

  const { edit, view, operation } = useSelectedOperations();

  function hasOpenedViewOrEditFor(id: number) {
    return jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPaymentPrep/edit/${id}`) || jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acPaymentPrep/view/${id}`);
  }

  const hasSelectedRow = !!selectedElement;
  const canBeDeleted = hasSelectedRow && !hasOpenedViewOrEditFor(selectedElement.id) && !selectedElement.void && listSec?.grantDel;
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;

  return (
    <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}>
      <div className="ifms-common-no-vertical-separator">
        <UniqueLinkButton name='paymentPrep-list-add-button' display="primary" size="regular" link={`/${operation}/acPaymentPrep/add`} disabled={!canBeAdd}
          label={<span><FontAwesomeIcon icon={faPlus} />&nbsp; New</span>} />&nbsp;
        <PrimaryButton name='paymentPrep-list-view-button' size="regular" disabled={!hasSelectedRow} onClick={view}><FontAwesomeIcon
          icon={faFileAlt} />&nbsp; View</PrimaryButton>&nbsp;
        <PrimaryButton name='paymentPrep-list-edit-button' size="regular" disabled={!canBeEdit} onClick={edit}><FontAwesomeIcon
          icon={faPencilAlt} />&nbsp; Edit</PrimaryButton>&nbsp;
        <PrimaryButton name='paymentPrep-list-delete-button' size="regular" disabled={!canBeDeleted} onClick={edit}><FontAwesomeIcon
          icon={faTrashAlt} />&nbsp; Delete</PrimaryButton>&nbsp;
      </div>
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({ onFunction: onFunctionKey });

  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctPaymentPrep);
  const { create, edit, view } = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
    if (!unprocessedFkey) {
      return;
    }

    doProcessFkey();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === 'F6') {

    } else if (unprocessedFkey === "F2") {
      if(listSec?.grantAdd)
        create();
    } else if (unprocessedFkey === "F3") {
      if(listSec?.grantEdit)
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } 

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F6', 'F2', 'F3', 'F9', 'F11', 'F8'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <></>
  );
}

function formatData(data: Array<PaymentPrepListItem>) {
  return data.map(d => ({
    ...d,
  }));
}


/**
 * Concrete implementation for Payment Prep List screen
 *
 * @class PaymentPrepListView
 * @extends {AbstractListView}
 */
class PaymentPrepListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.PaymentPrep,
      'PaymentPrepListItem',
      PaymentPrepListItem.calculateSearchParameters(search, PaymentPrepListItem.MapSearchFields),
      1,
      search.pageSize,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return { data: [], totalCount: 0 };
    }

    return { data: formatData(apiResult.entities), totalCount: apiResult.totalCount };
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {
    return PaymentPrepListItem.getExportCollection().map(x => {
      const colRenderer: ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if (x.type === XCol.XCellType.Double) colRenderer.Cell = (props: any) => <FloatRenderer {...props} decimals={2} />;
      else if (x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if (x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if (x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if (x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'paymentDate',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings = paymentPrepList;
    const cols = PaymentPrepListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type };
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands />
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar />
    );
  }

  renderExtras() {
    return <ListViewExtras />
  }

  getViewUrlFor(row: PaymentPrepListItem) {
    return row && row.id ? `/accounting/acPaymentPrep/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Payment Prep List';
  };

  renderAdditionalCommands() {
    return <JobListSuntekCommand jobName={JobNames.PaymentPrep} keyProp='id' showAdjustBalance={false} />;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  hasMemo(): boolean {
    return false;
  }
}

export default PaymentPrepListView;